import { useMemo } from "react";
import { supabase } from "./supabase";
import { useCachedQuery } from "./useCachedQuery";

// Techniques catalog — static reference data, mirrors useBadges' shape.
// Backed by the `techniques` table (migration 0178). Public-read,
// service-role write. 7-day TTL because rows change on admin curation
// cadence, not user activity.
//
// What this hook serves:
//
//   1. The "Explain this technique →" surface in CookMode. Renders
//      long_description, doneness cues, common pitfalls when the cook
//      taps an underlined technique-named verb mid-step.
//   2. (Later) A post-generation matcher that tokenizes recipe steps,
//      matches aliases[] against verb+object pairs, and either rewrites
//      ambiguous prose with the catalog's parameters or hangs an
//      annotation. The matcher lives separately; this hook just
//      delivers the rows.
//
// Egress: ~22 rows × ~2KB each = ~45KB on cache-miss; zero on cache
// hit. Single fetch per device per 7 days. No realtime subscription —
// the catalog isn't user-facing-mutable and realtime on a global table
// is the egress trap we already paid for once.

const CACHE_KEY = "techniques.catalog.v1";
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

function rowToTechnique(row) {
  return {
    slug:             row.slug,
    name:             row.name,
    category:         row.category,
    shortDescription: row.short_description,
    longDescription:  row.long_description,
    parameters:       row.parameters || {},
    donenessCues:     Array.isArray(row.doneness_cues)   ? row.doneness_cues   : [],
    commonPitfalls:   Array.isArray(row.common_pitfalls) ? row.common_pitfalls : [],
    relatedSlugs:     Array.isArray(row.related_slugs)   ? row.related_slugs   : [],
    aliases:          Array.isArray(row.aliases)         ? row.aliases         : [],
    animationId:      row.animation_id || null,
  };
}

async function loadTechniques() {
  const { data, error } = await supabase
    .from("techniques")
    .select("*")
    .order("category", { ascending: true })
    .order("name", { ascending: true });
  if (error) {
    console.warn("[techniques] catalog load failed:", error.message);
    return [];
  }
  return (data || []).map(rowToTechnique);
}

export function useTechniques() {
  const { data, loading, refresh } = useCachedQuery({
    cacheKey: CACHE_KEY,
    ttlMs: TTL_MS,
    fetcher: loadTechniques,
    defaultValue: [],
  });

  const techniques = data || [];

  // O(1) lookup by slug — the "Explain this" surface and the future
  // matcher both want this shape.
  const bySlug = useMemo(() => {
    const m = new Map();
    for (const t of techniques) m.set(t.slug, t);
    return m;
  }, [techniques]);

  // Grouped by category for catalog browsing (settings page, learn
  // surface, etc.). Categories preserved in declaration order so the
  // UI can render mixing → heating → doughs → prep → finishing without
  // sorting per-render.
  const byCategory = useMemo(() => {
    const groups = new Map();
    for (const t of techniques) {
      if (!groups.has(t.category)) groups.set(t.category, []);
      groups.get(t.category).push(t);
    }
    return groups;
  }, [techniques]);

  return { techniques, bySlug, byCategory, loading, refresh };
}

// Standalone lookup helper for non-React call sites (edge functions
// will eventually want their own load path; the matcher might run
// outside a component). Returns null when slug isn't in the catalog.
export function findTechniqueBySlug(catalog, slug) {
  if (!slug || !Array.isArray(catalog)) return null;
  return catalog.find((t) => t.slug === slug) || null;
}
